import React, { useState, useEffect } from 'react'
import { Modal, Slider, Button, Row, Col, Typography } from 'antd'
import VideoPlayer from './VideoPlayer' // Import the local wrapper which now supports volume
import { SoundOutlined, MutedOutlined } from '@ant-design/icons'

const { Text } = Typography

const PREVIEW_HEIGHT = 250

const ShotVolumeSettings = ({
  visible,
  onCancel,
  onSave,
  onApplyAll,
  videoUrl,
  initialVolume = 25
}) => {
  const [volume, setVolume] = useState(initialVolume)
  const [prevVolume, setPrevVolume] = useState(initialVolume > 0 ? initialVolume : 25)

  // Reset volume when modal opens with new initialValue
  useEffect(() => {
    if (visible) {
      setVolume(initialVolume)
      if (initialVolume > 0) {
        setPrevVolume(initialVolume)
      }
    }
  }, [visible, initialVolume])

  const handleSliderChange = value => {
    setVolume(value)
  }

  const toggleMute = () => {
    if (volume > 0) {
      setPrevVolume(volume)
      setVolume(0)
    } else {
      setVolume(prevVolume > 0 ? prevVolume : 25)
    }
  }

  const handleSave = () => {
    onSave(volume)
  }

  const handleApplyAllConfirm = () => {
    Modal.confirm({
      title: `Apply ${volume}% volume to all shots?`,
      content:
        'This volume level will be applied to every shot across all scenes.',
      okText: 'Apply to All',
      cancelText: 'Cancel',
      centered: true,
      onOk: () => {
        if (onApplyAll) {
          onApplyAll(volume)
        }
      }
    })
  }

  return (
    <Modal
      maskClosable={false}
      title="Shot Volume Settings"
      visible={visible}
      centered
      onCancel={onCancel}
      footer={[
        <Button
          key="apply-all"
          type="link"
          onClick={handleApplyAllConfirm}
          disabled={!onApplyAll}
          style={{ float: 'left', paddingLeft: 0 }}
        >
          Apply to all shots
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Apply to this shot
        </Button>
      ]}
      width={600}
      destroyOnClose
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div
          style={{
            width: '100%',
            height: PREVIEW_HEIGHT,
            maxHeight: '45vh',
            minHeight: 180,
            background: '#000',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {videoUrl ? (
            <VideoPlayer
              url={videoUrl}
              isVisible={true}
              volume={volume / 100}
              height="100%"
              showVolume={false}
            />
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}
            >
              No Video Available
            </div>
          )}
        </div>

        {/* Volume Controls */}
        <div
          style={{
            padding: '16px',
            background: '#f5f5f5',
            borderRadius: '8px'
          }}
        >
          <Text strong style={{ marginBottom: '4px', display: 'block' }}>
            Adjust Volume ({volume}%)
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: '12px', marginBottom: '16px', display: 'block' }}
          >
            This volume level will be applied to this shot when assembling the
            final video.
          </Text>
          <Row align="middle" gutter={16}>
            <Col flex="40px" style={{ textAlign: 'center' }}>
              {volume === 0 ? (
                <MutedOutlined
                  onClick={toggleMute}
                  style={{
                    cursor: 'pointer',
                    fontSize: '20px',
                    color: '#ff4d4f',
                    transition: 'transform 0.2s ease, color 0.2s ease'
                  }}
                  className="mute-icon-btn"
                  title="Unmute"
                />
              ) : (
                <SoundOutlined
                  onClick={toggleMute}
                  style={{
                    cursor: 'pointer',
                    fontSize: '20px',
                    color: '#1890ff',
                    transition: 'transform 0.2s ease, color 0.2s ease'
                  }}
                  className="mute-icon-btn"
                  title="Mute"
                />
              )}
            </Col>
            <Col flex="auto">
              <Slider
                min={0}
                max={100}
                value={volume}
                onChange={handleSliderChange}
                marks={{
                  0: 'Mute',
                  25: '25%',
                  50: '50%',
                  75: '75%',
                  100: '100%'
                }}
              />
            </Col>
          </Row>
        </div>
      </div>
    </Modal>
  )
}

export default ShotVolumeSettings
